import type { FilterGroup, FilterOption } from "@carbon6/c6-frontend-design";
import { EPISODE_TAG_ANY } from "@/api/datasetEpisode";
import {
  DATASET_FACET_UNSPECIFIED_TOKENS,
  type DatasetFacetAxis,
  type DatasetFacets,
} from "@/api/datasetFacets";
import { isCategoryChoiceValue } from "@/components/label";
import { cameraCountOption } from "./datasetFilters";

/**
 * 필터 카탈로그 그룹에 facets 개수를 추가한다. 순수 계산이다.
 *
 * **선택지는 카탈로그가 소유하고, facets 는 수만 준다.** 값으로 조인해 `count` 만 채운다. 옵션을
 * 빼거나 숨기지 않는다. 옵션이 사라지면 c6 `setGroups` 재정규화가 이미 적용한 값을 경고 없이
 * 버린다. 0 건도 `0` 배지로 남긴다. 예외는 카메라 수 하나다. 카탈로그 프리셋(0~6)보다 큰 값이
 * facets 에 오면 같은 모양의 옵션을 뒤에 추가한다.
 *
 * 수의 뜻은 모든 축에서 같다. 그 토큰 하나로 거른 같은 폴더 목록의 total 이다. 미지정 옵션
 * (`미지정`·`미상`·`결함 없음`)은 `unspecified` 를 쓰고, `결함 있음` 은 `total − unspecified` 를 쓴다.
 * 에피소드 결함 갈래 행은 배지가 없다. 서버가 데이터셋 단위 갈래 수를 주지 않기 때문이다.
 * 값 수를 더하면 한 데이터셋을 여러 번 센다.
 */
export function applyDatasetFacetCounts(
  groups: readonly FilterGroup[],
  facets: DatasetFacets,
): readonly FilterGroup[] {
  return groups.map((group) => {
    const axis = facetAxis(facets, group.value);
    if (axis === null) {
      return group;
    }
    const filters: FilterOption[] = group.filters.map((option) =>
      withFacetCount(option, countOf(group.value, option.value, axis, facets.total)),
    );
    if (group.value === "camera_count") {
      const known = new Set(group.filters.map((option) => option.value));
      for (const [value, count] of axis.byValue) {
        if (!known.has(value)) {
          filters.push({ ...cameraCountOption(value), count });
        }
      }
    }
    return { ...group, filters };
  });
}

/**
 * 개수만 제거한 사본을 만든다. 모집단이 다른 탭(보관됨·북마크)으로 바뀔 때 쓴다. 옵션 목록은
 * 그대로 둔다(추가한 카메라 수 포함). 그래서 적용한 선택이 유지된다.
 */
export function stripFacetCounts(groups: readonly FilterGroup[]): readonly FilterGroup[] {
  return groups.map((group) => ({
    ...group,
    filters: group.filters.map((option) => withFacetCount(option, undefined)),
  }));
}

type AxisCounts = {
  // 선택지 값 → 수(응답 순서)다. 카탈로그 축은 name 으로 묶어 합산한다.
  byValue: ReadonlyMap<string, number>;
  unspecified: number;
};

function facetAxis(facets: DatasetFacets, key: string): AxisCounts | null {
  if (key === "total" || !Object.prototype.hasOwnProperty.call(facets, key)) {
    return null;
  }
  const axis = facets[key as keyof DatasetFacets] as DatasetFacetAxis<
    { value: string; count: number } | { name: string; count: number }
  >;
  const byValue = new Map<string, number>();
  for (const option of axis.options) {
    // 카탈로그 행은 name 이 같고 tag 만 다른 여러 줄일 수 있다. 목록 쿼리 토큰이 name 이므로 합친다.
    const value = "value" in option ? option.value : option.name;
    byValue.set(value, (byValue.get(value) ?? 0) + option.count);
  }
  return { byValue, unspecified: axis.unspecified };
}

function countOf(key: string, value: string, axis: AxisCounts, total: number): number | undefined {
  const found = axis.byValue.get(value);
  if (found !== undefined) {
    return found;
  }
  if (value === DATASET_FACET_UNSPECIFIED_TOKENS[key]) {
    return axis.unspecified;
  }
  if (key === "episode_quality" && value === EPISODE_TAG_ANY) {
    // `결함 있음` 은 미지정(결함 에피소드 없음)의 여집합이다.
    return total - axis.unspecified;
  }
  if (key === "episode_quality" && isCategoryChoiceValue(value)) {
    return undefined;
  }
  // 서버는 값이 0 인 선택지도 모두 보낸다. 그래서 응답에 없는 값은 그 폴더에 하나도 없는 값이다.
  return 0;
}

// count 가 없으면 키를 빼서 c6 가 배지를 그리지 않게 한다(`0` 은 배지로 그린다).
function withFacetCount(option: FilterOption, count: number | undefined): FilterOption {
  const next = { ...option };
  delete next.count;
  return count === undefined ? next : { ...next, count };
}
